import { SplitPane } from '@/components/layout/SplitPane';
import { FreshnessPill } from '@/components/observability/FreshnessPill';
import { SourcePill } from '@/components/observability/SourcePill';
import { DrawToolbox } from '@/features/map/DrawToolbox';
import { MapCanvas } from '@/features/map/MapCanvas';
import { useMapSelectionStore } from '@/features/map/selection-store';
import { isInsideBbox, useMapViewportStore } from '@/features/map/viewport-store';
import { FilterBar } from '@/features/onus/FilterBar';
import { OnuDrawer } from '@/features/onus/OnuDrawer';
import { OnuTable } from '@/features/onus/OnuTable';
import { applyFilters } from '@/features/onus/filters';
import type { Onu } from '@/features/onus/types';
import { useOnuFilters } from '@/features/onus/useFilters';
import { useProgressiveSnapshot, type useSnapshot } from '@/features/onus/useSnapshot';
import { SelectionActionsBar } from '@/features/selection/SelectionActionsBar';
import { KpiStrip } from '@/features/stats/KpiStrip';
import { useZonesMap } from '@/features/zones/useZonesQuery';
import { useHotkey } from '@/hooks/use-hotkey';
import { isPointInFeature } from '@/lib/geo';
import { type PanelKey, usePanelMeta } from '@/lib/panel-meta-store';
import { useUiStore } from '@/lib/ui-store';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

/**
 * Cockpit — layout split-pane (mapa | tabela) — TD27-01 / Fase 1 do ADR-0004.
 *
 * Fonte de dados: `useSnapshot({ status: 'all' })` puxa o universo completo
 * em chunks progressivos de 2000 (useInfiniteQuery autopaginando). Mapa e
 * tabela crescem juntos conforme cada chunk chega — "ganham vida" no lugar
 * de spinner. Push delta WS (useLiveWS) patcha pages in-place no flip.
 *
 * Filtros (status + age) aplicam **client-side** sobre o array crescente.
 * URL é source-of-truth (nuqs). Mapa SEMPRE recebe `filtered` completo
 * (nunca pagina) — escopo coerente com a tabela.
 *
 * Loading states:
 * - `isPending`: nem a primeira page chegou → skeleton
 * - `isLoadingMore`: pages chegando (loaded / total visível no header)
 * - `isComplete`: tudo carregado → indicador "atualizado há Xs"
 * - `isError`: alguma page falhou → empty hint contextual
 */
export const Route = createFileRoute('/_auth/cockpit')({
  component: CockpitPage,
});

function CockpitPage() {
  const ratio = useUiStore((s) => s.cockpitSplitRatio);
  const setRatio = useUiStore((s) => s.setCockpitSplitRatio);
  const { status, age, q, olt, bbox, polygonId, reset, setPolygonId } = useOnuFilters();
  const mapBbox = useMapViewportStore((s) => s.bbox); // TD27-06

  // TD27-09: selection store + zones lookup
  const draft = useMapSelectionStore((s) => s.draft);
  const focusOnly = useMapSelectionStore((s) => s.focusOnly);
  const setDraft = useMapSelectionStore((s) => s.setDraft);
  const setMode = useMapSelectionStore((s) => s.setMode);
  const clearSelection = useMapSelectionStore((s) => s.clearAll);
  const zonesMap = useZonesMap();

  // Bootstrap bad-first → all (TD26 perf): paint instantâneo ~360 ONUs bad
  // (≈150ms), depois canonicalmente vira all (≈1.5s). Operador vê dados úteis
  // imediato; quando all chega, substitui sem flash (bad ⊂ all).
  const snapshot = useProgressiveSnapshot();

  // TD27-06: bbox predicate só ativo quando flag URL `?bbox=1` E mapa
  // publicou viewport. Sem mapa inicializado, ignora (não rejeita tudo).
  const bboxPredicate = useMemo(() => {
    if (!bbox || !mapBbox) return undefined;
    return (o: Onu) => isInsideBbox(o.lng, o.lat, mapBbox);
  }, [bbox, mapBbox]);

  // TD27-09 — URL hydration: ?polygonId=<uuid> carrega a zona no store.
  // Acontece quando o user (a) abre URL compartilhada, (b) refresh, (c) navega
  // de outra view pra cockpit já com polygonId setado. Sem isso, predicate
  // ficaria undefined e a tabela mostraria universo todo apesar do URL.
  useEffect(() => {
    if (!polygonId) {
      // Se URL não tem polygonId e store tem zone-loaded, limpa.
      if (draft && useMapSelectionStore.getState().source === 'zone') {
        clearSelection();
      }
      return;
    }
    const zone = zonesMap.get(polygonId);
    if (!zone) return;
    // Se já está carregada a mesma zona, no-op
    const currentSource = useMapSelectionStore.getState().source;
    const currentDraft = useMapSelectionStore.getState().draft;
    if (
      currentSource === 'zone' &&
      currentDraft &&
      JSON.stringify(currentDraft.geometry) === JSON.stringify(zone.polygon.geometry)
    ) {
      return;
    }
    setDraft(zone.polygon as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>, 'zone');
  }, [polygonId, zonesMap, draft, setDraft, clearSelection]);

  // TD27-09 — polygon predicate (point-in-polygon). Roda sobre snapshot.items
  // a cada mudança em `draft` — barato (~5ms pra 9k pontos).
  const polygonPredicate = useMemo(() => {
    if (!draft) return undefined;
    return (o: Onu) => {
      if (o.lng == null || o.lat == null) return false;
      return isPointInFeature(o.lng, o.lat, draft);
    };
  }, [draft]);

  // Filtros base (sem polygon) — usado pra calcular o "universo após filtros normais"
  const filteredBeforePolygon = useMemo(
    () => applyFilters(snapshot.items, { status, age, q, olt, bboxPredicate }),
    [snapshot.items, status, age, q, olt, bboxPredicate],
  );

  // Subset dentro do polygon ativo (sempre derivado — mesmo se focusOnly=false,
  // precisamos pra SelectionActionsBar + highlight no MapCanvas/OnuTable).
  const insidePolygon = useMemo(() => {
    if (!polygonPredicate) return filteredBeforePolygon;
    return filteredBeforePolygon.filter(polygonPredicate);
  }, [filteredBeforePolygon, polygonPredicate]);

  // `filtered` é o que mapa+tabela consomem. Se focusOnly e há polygon ativo,
  // colapsa pro subset. Senão, mostra o universo filtrado (highlight pattern).
  const filtered = focusOnly && draft ? insidePolygon : filteredBeforePolygon;

  // SNs dentro do polygon — pro MapCanvas dimming + OnuTable highlight rows
  const selectionSns = useMemo<ReadonlySet<string> | null>(() => {
    if (!draft) return null;
    return new Set(insidePolygon.map((o) => o.sn));
  }, [draft, insidePolygon]);

  const hasActiveFilters =
    status.length > 0 || age !== 'any' || q.length > 0 || olt.length > 0 || bbox || draft !== null;

  // TD27-09 — hotkeys de toolbox + escalonamento do Esc
  useHotkey('r', () => setMode('draw_rectangle'));
  useHotkey('p', () => setMode('draw_polygon'));
  useHotkey('c', () => setMode('draw_circle'));
  useHotkey('shift+f', () => setMode('draw_freehand'));
  useHotkey('escape', () => {
    // Esc escalado: 1º cancela modo de desenho ativo; 2º limpa polygon.
    const state = useMapSelectionStore.getState();
    if (state.mode) {
      setMode(null);
    } else if (state.draft) {
      clearSelection();
      setPolygonId(null);
    }
  });

  return (
    <div className="relative flex h-full flex-col">
      <KpiStrip />
      <div className="relative min-h-0 flex-1">
        <SplitPane
          ratio={ratio}
          onRatioChange={setRatio}
          ariaLabel="Redimensionar entre mapa e tabela"
          minLeft={320}
          minRight={420}
          left={
            <MapPanel
              data={filtered}
              snapshot={snapshot}
              selectionSns={selectionSns}
              insidePolygon={insidePolygon}
              polygonActive={draft !== null}
            />
          }
          right={
            <TablePanel
              data={filtered}
              snapshot={snapshot}
              hasActiveFilters={hasActiveFilters}
              onReset={reset}
              selectionSns={selectionSns}
              selectionCount={insidePolygon.length}
            />
          }
        />
        <OnuDrawer data={filtered} />
      </div>
    </div>
  );
}

type SnapshotState = ReturnType<typeof useSnapshot>;

function MapPanel({
  data,
  snapshot,
  selectionSns,
  insidePolygon,
  polygonActive,
}: {
  data: Onu[];
  snapshot: SnapshotState;
  selectionSns: ReadonlySet<string> | null;
  insidePolygon: Onu[];
  polygonActive: boolean;
}) {
  const selectedSn = useUiStore((s) => s.selectedOnuSn);
  const setSelectedSn = useUiStore((s) => s.setSelectedOnuSn);

  // PR 1.7.4: separa ONUs com geo das sem geo. Mapa só renderiza as com geo
  // (lat/lng !== null); contador "N sem geo" no header dá feedback ao operador
  // pra não pensar que filtro "não bateu nada" quando na verdade as ONUs
  // existem mas o Voalle não tem endereço cadastrado.
  // useMemo: sem isso `withGeo` é nova ref a cada render → MapCanvas useEffect
  // [onus] dispara setData a cada render mesmo sem mudanças reais; combinado
  // com easeTo cascade gera churn pesado (ver MapCanvas.tsx).
  const withGeo = useMemo(() => data.filter((o) => o.lat != null && o.lng != null), [data]);
  const withoutGeo = data.length - withGeo.length;

  let hint: string;
  if (snapshot.isError) {
    hint = 'erro ao carregar ONUs';
  } else if (snapshot.isPending) {
    hint = 'carregando…';
  } else if (!snapshot.isComplete) {
    hint = `${withGeo.length} pins · carregando ${snapshot.loaded}/${snapshot.total}…`;
  } else {
    const tail = withoutGeo > 0 ? ` · ${withoutGeo} sem geo` : '';
    hint = `${withGeo.length} de ${snapshot.total} pins${tail} · click pra selecionar`;
  }

  return (
    <section aria-label="Painel do mapa" className="flex h-full w-full flex-col bg-bg-muted">
      <PanelHeader title="Mapa" hint={hint} snapshot={snapshot} />
      <div className="relative flex-1">
        <MapCanvas
          onus={withGeo}
          selectedSn={selectedSn}
          onSelect={setSelectedSn}
          selectionSns={selectionSns}
        />
        {/* TD27-09 — toolbox de desenho + actions bar pós-seleção */}
        <DrawToolbox />
        {polygonActive && <SelectionActionsBar selected={insidePolygon} />}
      </div>
    </section>
  );
}

function TablePanel({
  data,
  snapshot,
  hasActiveFilters,
  onReset,
  selectionSns,
  selectionCount,
}: {
  data: Onu[];
  snapshot: SnapshotState;
  hasActiveFilters: boolean;
  onReset: () => void;
  /** TD27-09 — SNs dentro do polygon pra highlight nas rows. */
  selectionSns: ReadonlySet<string> | null;
  /** Count pra SelectionScopePill no FilterBar. */
  selectionCount: number;
}) {
  const hint = snapshot.isError
    ? 'erro ao carregar ONUs'
    : snapshot.isPending
      ? 'carregando ONUs…'
      : !snapshot.isComplete
        ? `${data.length} · carregando ${snapshot.loaded}/${snapshot.total}…`
        : `${data.length} de ${snapshot.total} · filtros em URL`;

  // Empty states contextuais (TD27-04 / ADR §5.7):
  //  - isError                                       → falha backend
  //  - isPending && nada carregado                   → bootstrapping
  //  - isComplete && total === 0                     → poller ainda não populou
  //  - isComplete && data.length === 0 && filters    → filtro apertado
  //  - isComplete && data.length === 0 && !filters   → saudável (não esperado: status=[] = tudo)
  const showEmpty = !snapshot.isPending && data.length === 0;
  let emptyState: { icon: string; title: string; hint: string; cta?: string } | null = null;
  if (snapshot.isError) {
    emptyState = {
      icon: '⚠️',
      title: 'Falha ao carregar ONUs',
      hint: 'O backend respondeu com erro. Tentando de novo automaticamente.',
    };
  } else if (snapshot.isPending) {
    // Mostrar loading não-bloqueante (skeleton ficou no header)
    emptyState = null;
  } else if (snapshot.total === 0) {
    emptyState = {
      icon: '⏳',
      title: 'Aguardando primeiro ciclo do poller',
      hint: 'O sistema ainda não detectou ONUs. Espera de até 60s após o poller iniciar.',
    };
  } else if (data.length === 0 && hasActiveFilters) {
    emptyState = {
      icon: '🔍',
      title: 'Nenhuma ONU com esses filtros',
      hint: 'Relaxe um filtro ou limpe tudo pra ver o universo de novo.',
      cta: 'Limpar filtros',
    };
  } else if (data.length === 0 && !hasActiveFilters) {
    emptyState = {
      icon: '🎉',
      title: 'Nenhuma ONU pra mostrar',
      hint: 'Inventário vazio sem filtros — verificar mart.',
    };
  }

  return (
    <section aria-label="Painel da tabela de ONUs" className="flex h-full w-full flex-col bg-bg">
      <PanelHeader title="ONUs" hint={hint} snapshot={snapshot} />
      <FilterBar selectionCount={selectionCount} />
      <div className="min-h-0 flex-1">
        {showEmpty && emptyState ? (
          <EmptyState {...emptyState} onCta={onReset} />
        ) : (
          <OnuTable data={data} selectionSns={selectionSns} />
        )}
      </div>
    </section>
  );
}

function EmptyState({
  icon,
  title,
  hint,
  cta,
  onCta,
}: {
  icon: string;
  title: string;
  hint: string;
  cta?: string;
  onCta?: () => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <span className="text-3xl" aria-hidden>
        {icon}
      </span>
      <h2 className="text-sm font-medium text-fg">{title}</h2>
      <p className="max-w-xs text-xs text-fg-subtle">{hint}</p>
      {cta && onCta && (
        <button
          type="button"
          onClick={onCta}
          className="mt-1 rounded-md border border-border bg-bg px-3 py-1 text-xs text-fg-muted transition-colors hover:border-accent hover:text-fg"
        >
          {cta}
        </button>
      )}
    </div>
  );
}

function PanelHeader({
  title,
  hint,
  snapshot,
  metaKey,
}: {
  title: string;
  hint: string;
  snapshot: SnapshotState;
  /**
   * TD27-07/08 (Fase 2 ADR-0004): identifica qual meta o panel consume pra
   * renderizar SourcePill + FreshnessPill. Default 'snapshot' (mapa/tabela).
   */
  metaKey?: PanelKey;
}) {
  const meta = usePanelMeta(metaKey ?? 'snapshot');
  return (
    <header className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-bg-elevated px-3 text-xs">
      <span className="font-medium text-fg">{title}</span>
      {snapshot.isError ? (
        <span className="rounded bg-status-critical/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-status-critical">
          erro
        </span>
      ) : snapshot.isPending ? (
        <span className="rounded bg-bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-fg-subtle">
          carregando
        </span>
      ) : !snapshot.isComplete ? (
        // Barra fina de progresso enquanto chunks chegam — ADR-0004 §5.8.
        <span
          aria-label={`carregando ${Math.round(snapshot.progress * 100)}%`}
          className="relative inline-block h-1 w-12 overflow-hidden rounded-full bg-bg-muted"
        >
          <span
            className="absolute inset-y-0 left-0 bg-status-info transition-[width] duration-300"
            style={{ width: `${Math.round(snapshot.progress * 100)}%` }}
          />
        </span>
      ) : snapshot.isLoadingMore ? (
        <span
          aria-label="atualizando"
          className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-status-info"
        />
      ) : null}
      <SourcePill meta={meta} />
      <FreshnessPill meta={meta} />
      <span className="ml-auto truncate text-fg-subtle">{hint}</span>
    </header>
  );
}
